import { ResultValueType } from "../enums/result-value-type.enum";
import { ResultStatus } from "../enums/resultStatus.enum";
import { PanelResult, PanelResultDetails } from "../store/slices/order/types";
import { isNumber } from "./helper";

export const getPanelResulItemStatus = (
  panelResult: PanelResultDetails
): ResultStatus => {
  const { minValue, maxValue, strValue, valueType } = panelResult;

  if (!minValue || !maxValue || !strValue || !isNumber(maxValue))
    return ResultStatus.CannotBeDetermined;

  if (
    parseFloat(strValue) >= parseFloat(minValue) &&
    parseFloat(strValue) <= parseFloat(maxValue)
  ) {
    return ResultStatus.Good;
  } else {
    return ResultStatus.Abnormal;
  }
};

export const getPanelResultStatus = (
  availablePanelResultDetailsList: PanelResultDetails[]
) => {
  const resultStatuses = availablePanelResultDetailsList.map((result) =>
    getPanelResulItemStatus(result)
  );

  if (resultStatuses.includes(ResultStatus.Abnormal)) {
    return ResultStatus.Abnormal;
  }

  if (
    resultStatuses.every((resultStatus) => resultStatus === ResultStatus.Good)
  ) {
    return ResultStatus.Good;
  }

  return ResultStatus.CannotBeDetermined;
};

export const groupPanelResultDetailsByValueTypes = (
  availablePanelResultDetails: PanelResultDetails[]
) => {
  const otherResultTypes = [
    ResultValueType.String3,
    ResultValueType.String4,
    ResultValueType.String5,
    ResultValueType.String20,
  ];
  const intergerResults = availablePanelResultDetails.filter(
    (panelResultDetails) =>
      panelResultDetails.valueType === ResultValueType.Integer &&
      panelResultDetails.maxValue
  );
  const stringResults = availablePanelResultDetails.filter(
    (panelResultDetails) =>
      panelResultDetails.valueType === ResultValueType.String ||
      (panelResultDetails.valueType === ResultValueType.Integer &&
        !panelResultDetails.maxValue)
  );
  const otherResults = availablePanelResultDetails.filter(
    (panelResultDetails) =>
      otherResultTypes.includes(panelResultDetails.valueType as ResultValueType)
  );
  const edit1Results = availablePanelResultDetails.filter(
    (panelResultDetails) =>
      panelResultDetails.valueType === ResultValueType.Edit1
  );
  const lstResults = availablePanelResultDetails.filter(
    (panelResultDetails) => panelResultDetails.valueType === ResultValueType.LST
  );
  const cultureResults = availablePanelResultDetails.filter(
    (panelResultDetails) =>
      panelResultDetails.valueType === ResultValueType.Culture
  );

  return [
    ...intergerResults,
    ...stringResults,
    ...otherResults,
    ...lstResults,
    ...edit1Results,
    ...cultureResults,
  ];
};

export const getAvailablePanelResults = (panelResults: PanelResult[]) => {
  return panelResults.filter((panelResult) =>
    panelResult.panelResultDetails.some(
      (resultDetails) => resultDetails.strValue
    )
  );
};

export const formatCultureResultData = (cultureStringValue: string) => {
  const resultArr = cultureStringValue.split("&");

  const organisms = resultArr.filter((organism) => /^Org\d+_/.test(organism));

  const organismGrowths = resultArr.filter((item) => item.includes("GROWTH"));

  const orgClassification = organisms.map((organism: string) => {
    //extract the number;
    const orgNumber = (organism?.match(/Org(\d+)_/) as any)[1];

    //find the corresponding growth item
    const orgGrowth = organismGrowths.find((growth) =>
      growth.includes(`Org_GROWTH_${orgNumber}_`)
    );

    if (orgGrowth) {
      return {
        name: organism.split("=")[1],
        orgNumber: `Org${orgNumber}`,
        growth: orgGrowth.split("=")[1],
        orgGrowthNumber: orgGrowth.split("=")[0],
      };
    }
  });

  const allSensitivity = resultArr.filter((item) => item.includes("atb_"));

  const antibiotics = allSensitivity
    .filter(
      (sensitivity) =>
        sensitivity.includes("atb_") && !sensitivity.includes("_o_")
    )
    .map((item) => {
      const [, , index, name] = item.split(/=|_/);
      return { index, name };
    });

  const reactions = allSensitivity
    .filter((sensitivity) => sensitivity.includes("_o_"))
    .map((item) => {
      const [, , , orgIndex, atbIndex, reaction] = item.split(/=|_/);
      return { orgIndex, atbIndex, reaction };
    });

  const result = reactions.reduce(
    (
      acc: Array<{ reaction: string; antibiotic: string; org: string }>,
      { orgIndex, atbIndex, reaction }
    ) => {
      if (reaction) {
        const antibiotic =
          antibiotics.find((atb) => atb.index === atbIndex)?.name || "";
        acc.push({ antibiotic, reaction, org: `Org${orgIndex}` });
      }
      return acc;
    },
    []
  );

  const finalResult = orgClassification
    .map((orgClass) => {
      return {
        ...orgClass,
        sensitivity: result.filter((item) => item?.org === orgClass?.orgNumber),
      };
    })
    .filter((result) => result.sensitivity.length > 0 && result.name);

  return finalResult;
};
